import * as AccordionPrimitive from '@radix-ui/react-accordion';
import React from 'react';
import { ChevronDown } from '@/components/Icons/ChevronDown';
import { JustificationDateGroup } from '@/pages/Overview/JustificationDateGroup';
import type { NonAttendance, NonAttendanceGroup } from '@/types/overview';
import { sortByDateDesc } from '@/utils/date';
import { tv } from '@/utils/tv';

interface Props {
  title: string;
  entries: NonAttendanceGroup[];
  onAbsenceClick: (absence: NonAttendance, date: string) => void;
}

const triggerChevron = tv({
  base: 'size-4 text-foreground transition-transform duration-200 group-data-[state=open]:rotate-180',
  variants: {
    isEmpty: {
      true: 'invisible',
      false: '',
    },
  },
});

export const JustificationsCard: React.FC<Props> = ({
  title,
  entries,
  onAbsenceClick,
}) => {
  const count = entries.reduce(
    (sum, entry) => sum + entry.nonAttendances.length,
    0,
  );
  const sortedEntries = sortByDateDesc(entries, (entry) => entry.date);

  return (
    <AccordionPrimitive.Root
      type="single"
      collapsible
      className="flex w-full flex-col rounded-xl bg-popover shadow-card md:w-auto md:flex-1 md:self-start"
    >
      <AccordionPrimitive.Item value="root" className="flex flex-col">
        <AccordionPrimitive.Header className="flex">
          <AccordionPrimitive.Trigger
            disabled={count === 0}
            className="group flex w-full cursor-pointer items-center justify-between gap-2 px-5 py-4 text-left outline-none disabled:cursor-default"
          >
            <span className="text-body-16 text-subtle-foreground">{title}</span>
            <span className="flex shrink-0 items-center gap-2">
              <span className="text-subhead-14 text-primary">{count}</span>
              <ChevronDown
                className={triggerChevron({ isEmpty: count === 0 })}
              />
            </span>
          </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
        <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
          <AccordionPrimitive.Root
            type="multiple"
            className="flex flex-col border-t border-border"
          >
            {sortedEntries.map((entry) => (
              <JustificationDateGroup
                key={entry.date}
                entry={entry}
                value={entry.date}
                onAbsenceClick={onAbsenceClick}
              />
            ))}
          </AccordionPrimitive.Root>
        </AccordionPrimitive.Content>
      </AccordionPrimitive.Item>
    </AccordionPrimitive.Root>
  );
};
